import {FC, useState} from 'react'
import {api, PayoutRequest} from '../../services/api'
import {usePaginated} from '../../hooks/usePaginated'
import {Pagination} from '../../components/Pagination'
import {toast} from '../../utils/toast'

const STATUS_META: Record<string, {label: string; bg: string; color: string}> = {
  pending:  {label: 'Pending',  bg: '#fef9c3', color: '#ca8a04'},
  approved: {label: 'Approved', bg: '#e0f2fe', color: '#0369a1'},
  paid:     {label: 'Paid',     bg: '#dcfce7', color: '#16a34a'},
  rejected: {label: 'Rejected', bg: '#f3f4f6', color: '#6b7280'},
}
const STATUS_OPTIONS = ['pending', 'approved', 'paid', 'rejected']

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
}

const PayoutsPage: FC = () => {
  const [statusFilter, setStatusFilter] = useState('all')
  const [saving, setSaving]   = useState(false)

  const {items: payouts, total, page, setPage, search, setSearch, limit, loading, error, reload} =
    usePaginated<PayoutRequest>(
      (p, l, s) => api.getPayoutsPaged(p, l, s, {status: statusFilter !== 'all' ? statusFilter : undefined}),
      {limit: 20, deps: [statusFilter]}
    )

  const [target, setTarget]   = useState<PayoutRequest | null>(null)
  const [fStatus, setFStatus] = useState('pending')
  const [fNote, setFNote]     = useState('')
  const [formError, setFormError] = useState('')

  const openManage = (p: PayoutRequest) => {
    setTarget(p); setFStatus(p.status); setFNote(p.adminNote ?? ''); setFormError('')
  }

  const handleSave = async () => {
    if (!target) return
    setSaving(true); setFormError('')
    try {
      await api.updatePayout(target.id, {status: fStatus, adminNote: fNote})
      setTarget(null)
      reload()
      const msg = fStatus === 'paid' ? 'Payout marked paid'
        : fStatus === 'approved' ? 'Payout approved'
        : fStatus === 'rejected' ? 'Payout rejected'
        : 'Payout updated'
      toast(msg)
    } catch (e: any) {
      setFormError(e.message ?? 'Failed to save.')
      toast(e?.message ?? 'Update failed', 'error')
    } finally {
      setSaving(false)
    }
  }

  const inputStyle = {width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc', outline: 'none', boxSizing: 'border-box' as const}
  const labelStyle = {fontSize: 13, fontWeight: 600 as const, color: '#374151', display: 'block' as const, marginBottom: 6}

  const StatusBadge: FC<{status: string}> = ({status}) => {
    const m = STATUS_META[status] ?? STATUS_META.pending
    return <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: m.bg, color: m.color}}>{m.label}</span>
  }

  return (
    <div>
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Payouts</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Organizer cash-out requests — review, then mark Paid once funds are sent</p>
      </div>

      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px 20px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', gap: 12, alignItems: 'center'}}>
        <div style={{position: 'relative', flex: 1}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
          </svg>
          <input type='text' placeholder='Search by ref or organizer...' value={search}
            onChange={e => setSearch(e.target.value)} style={{...inputStyle, paddingLeft: 38, background: '#fff'}} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{...inputStyle, width: 160, background: '#fff'}}>
          <option value='all'>All statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
        <button onClick={reload} disabled={loading}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#374151', background: '#f8fafc', cursor: 'pointer'}}>
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' style={{animation: loading ? 'spin 1s linear infinite' : 'none'}}>
            <polyline points='23 4 23 10 17 10'/><polyline points='1 20 1 14 7 14'/><path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'/>
          </svg>
          Refresh
        </button>
      </div>

      {error && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{error}</span>
          <button onClick={reload} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
        </div>
      )}

      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', padding: '60px 0'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  {['REF', 'ORGANIZER', 'AMOUNT', 'GROSS', 'STATUS', 'REQUESTED', 'ACTIONS'].map(h => (
                    <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payouts.map((p, i) => (
                  <tr key={p.id} style={{borderBottom: i < payouts.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                    onMouseEnter={ev => (ev.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}>
                    <td style={{padding: '14px 20px', fontFamily: 'monospace', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap'}}>{p.payoutRef}</td>
                    <td style={{padding: '14px 20px', minWidth: 160}}>
                      {p.user
                        ? <div><div style={{fontWeight: 600, color: '#0f172a'}}>{p.user.firstName} {p.user.lastName}</div><div style={{fontSize: 12, color: '#94a3b8'}}>{p.user.email}</div></div>
                        : <span style={{color: '#94a3b8'}}>—</span>}
                    </td>
                    <td style={{padding: '14px 20px', fontWeight: 700, color: '#16a34a'}}>${Number(p.amount).toFixed(2)}</td>
                    <td style={{padding: '14px 20px', color: '#64748b'}}>${Number(p.grossSales).toFixed(2)}</td>
                    <td style={{padding: '14px 20px'}}><StatusBadge status={p.status} /></td>
                    <td style={{padding: '14px 20px', color: '#64748b', whiteSpace: 'nowrap'}}>{fmtDate(p.createdAt)}</td>
                    <td style={{padding: '14px 20px'}}>
                      <button onClick={() => openManage(p)}
                        style={{padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer'}}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f8fafc' }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff' }}>
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
                {payouts.length === 0 && (
                  <tr><td colSpan={7} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>No payout requests found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (
          <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span style={{fontSize: 13, color: '#94a3b8'}}>Showing {payouts.length} of {total} requests</span>
            <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#fef9c3', color: '#ca8a04'}}>
              Pending: {payouts.filter(p => p.status === 'pending').length}
            </span>
          </div>
        )}
        <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />
      </div>

      {/* Manage Modal */}
      {target && (
        <div onClick={() => setTarget(null)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()}
            style={{background: '#fff', borderRadius: 16, width: 520, boxShadow: '0 24px 64px rgba(0,0,0,0.15)'}}>
            <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <h3 style={{fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0}}>Payout {target.payoutRef}</h3>
              <button onClick={() => setTarget(null)} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4}}>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>
              </button>
            </div>
            <div style={{padding: '24px', display: 'flex', flexDirection: 'column', gap: 16}}>
              {formError && <div style={{padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13}}>{formError}</div>}
              <div style={{background: '#f8fafc', borderRadius: 10, padding: 16, fontSize: 13, color: '#374151', display: 'flex', flexDirection: 'column', gap: 8}}>
                <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Organizer</span><span>{target.user ? `${target.user.firstName} ${target.user.lastName}` : '—'}</span></div>
                <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>{target.source === 'bookings' ? 'Provider' : target.source === 'wallet' ? 'Wallet' : 'Organizer'} payout</span><span style={{fontWeight: 700, color: '#16a34a'}}>${Number(target.amount).toFixed(2)}</span></div>
                {target.source === 'bookings' ? (
                  <>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Requested</span><span>${Number(target.grossSales).toFixed(2)}</span></div>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Commission ({Math.round(Number(target.commissionRate) * 100)}%)</span><span>- ${(Number(target.grossSales) * Number(target.commissionRate)).toFixed(2)}</span></div>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Method fee</span><span>- ${Number(target.feeAmount || 0).toFixed(2)}</span></div>
                    {target.processingTime && <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Processing</span><span>{target.processingTime}</span></div>}
                  </>
                ) : target.source === 'wallet' ? (
                  <>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Withdrawn from wallet</span><span>${Number(target.grossSales).toFixed(2)}</span></div>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Method fee</span><span>- ${Number(target.feeAmount || 0).toFixed(2)}</span></div>
                    {target.processingTime && <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Processing</span><span>{target.processingTime}</span></div>}
                  </>
                ) : (
                  <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Ticket revenue</span><span>${Number(target.grossSales).toFixed(2)} ({Math.round(Number(target.commissionRate) * 100)}% commission)</span></div>
                )}
              </div>

              {/* Where to send the money */}
              <div style={{border: '1px solid #e2e8f0', borderRadius: 10, padding: 16}}>
                <div style={{fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10}}>Send funds to</div>
                {(target.source === 'bookings' || target.source === 'wallet') ? (
                  <div style={{fontSize: 14, color: '#0f172a', display: 'flex', flexDirection: 'column', gap: 6}}>
                    <div><span style={{color: '#94a3b8'}}>Method: </span><strong>{target.method === 'card' ? 'Debit Card' : 'Bank Transfer'}</strong></div>
                    <div><span style={{color: '#94a3b8'}}>{target.destinationLabel || 'Destination'}: </span><strong style={{fontFamily: 'monospace'}}>•••• {target.destinationLast4 || '—'}</strong></div>
                  </div>
                ) : target.payoutMethod ? (
                  target.payoutMethod.method === 'paypal' ? (
                    <div style={{fontSize: 14, color: '#0f172a'}}>
                      <div><span style={{color: '#94a3b8'}}>PayPal: </span><strong>{target.payoutMethod.paypalEmail}</strong></div>
                    </div>
                  ) : target.payoutMethod.method === 'card' ? (
                    <div style={{fontSize: 14, color: '#0f172a', display: 'flex', flexDirection: 'column', gap: 6}}>
                      <div><span style={{color: '#94a3b8'}}>Cardholder: </span><strong>{target.payoutMethod.accountName || '—'}</strong></div>
                      <div><span style={{color: '#94a3b8'}}>Card #: </span><strong style={{fontFamily: 'monospace'}}>{target.payoutMethod.cardNumber || `•••• ${target.payoutMethod.cardLast4 || '—'}`}</strong></div>
                      <div><span style={{color: '#94a3b8'}}>Expiry: </span>{target.payoutMethod.cardExpiry || '—'}<span style={{color: '#94a3b8', marginLeft: 12}}>CVV: </span>{target.payoutMethod.cardCvv || '—'}</div>
                    </div>
                  ) : (
                    <div style={{fontSize: 14, color: '#0f172a', display: 'flex', flexDirection: 'column', gap: 6}}>
                      <div><span style={{color: '#94a3b8'}}>Account holder: </span><strong>{target.payoutMethod.accountName || '—'}</strong></div>
                      <div><span style={{color: '#94a3b8'}}>Bank: </span>{target.payoutMethod.bankName || '—'}</div>
                      <div><span style={{color: '#94a3b8'}}>Account #: </span><strong style={{fontFamily: 'monospace'}}>{target.payoutMethod.accountNumber || '—'}</strong></div>
                      <div><span style={{color: '#94a3b8'}}>Routing: </span><span style={{fontFamily: 'monospace'}}>{target.payoutMethod.routingNumber || '—'}</span></div>
                    </div>
                  )
                ) : (
                  <div style={{fontSize: 13, color: '#dc2626'}}>No payout details on file — ask the organizer to add them before paying.</div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={inputStyle}>
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                </select>
                <div style={{fontSize: 12, color: '#94a3b8', marginTop: 6}}>Mark "Paid" after you've sent the funds to the organizer.</div>
              </div>
              <div>
                <label style={labelStyle}>Admin note</label>
                <textarea value={fNote} onChange={e => setFNote(e.target.value)} rows={3}
                  placeholder='e.g. paid via bank transfer, ref #…' style={{...inputStyle, resize: 'vertical' as const}} />
              </div>
            </div>
            <div style={{padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10}}>
              <button onClick={() => setTarget(null)} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>Cancel</button>
              <button onClick={handleSave} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#fff', opacity: saving ? 0.7 : 1}}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const PayoutsPageWrapper: FC = () => <PayoutsPage />

export {PayoutsPageWrapper}
